import {
    IconButton,
    MenuList,
    MenuItem,
    Menu,
    MenuButton,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    VStack,
    HStack,
    Flex,
    Icon,
    Heading,
    Box,
    Link as ChakraLink,
} from '@chakra-ui/react';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { FaClipboardList } from 'react-icons/fa';
import { FiClock, FiFlag, FiCheck, FiCheckCircle, FiSkipForward, FiEye, FiX, FiAlertTriangle, FiExternalLink } from 'react-icons/fi';
import { ORDER_ACTION_ICONS } from '../../utils/orderActionIcons';
import { useContext, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ReKeyingEntry } from '../../hooks/useGetOrdersFeed';
import { useCompleteOrder } from '../../hooks/useCompleteOrder';
import { useArchiveOrder } from '../../hooks/useArchiveOrder';
import SessionContext from '../../contexts/SessionContext';
import { Vin, NoteCountBadge, SupplierCommunicationCountBadge, EnterEstimateBadge } from './components';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import ResizableTh from '../ResizableTh';
import { STICKY_TABLE_HEADER_Z_INDEX } from '../../utils/tableCopySx';
import {
    DashboardRoColumnRow,
    DashboardRoSelectAllHeader,
    type DashboardBulkSelection,
    getDashboardBulkTableSx,
    getDashboardRoColumnPl,
    getDashboardRowHighlight,
    handleOrderBulkRowClick,
} from './dashboardBulkSelection';

interface RekeyingStatusBadgeProps {
    status: ReKeyingEntry['status'];
}

const RekeyingStatusBadge = ({ status }: RekeyingStatusBadgeProps) => {
    const config = useMemo(() => {
        const statusConfig = {
            'not-started': {
                icon: FiClock,
                bg: 'gray.100',
                color: 'gray.600',
                label: 'Not Started'
            },
            'flagged': {
                icon: FiFlag,
                bg: 'orange.100',
                color: 'orange.800',
                label: 'Flagged'
            },
            'completed': {
                icon: FiCheck,
                bg: 'green.100',
                color: 'green.800',
                label: 'Completed'
            },
            'skipped': {
                icon: FiSkipForward,
                bg: 'gray.100',
                color: 'gray.600',
                label: 'Skipped'
            },
            'in-review': {
                icon: FiEye,
                bg: 'blue.100',
                color: 'blue.800',
                label: 'In Review'
            },
            'failed': {
                icon: FiX,
                bg: 'red.100',
                color: 'red.800',
                label: 'Failed'
            },
            'advisor': {
                icon: FiAlertTriangle,
                bg: 'yellow.100',
                color: 'yellow.800',
                label: 'Advisor'
            },
            'needed': {
                icon: FiClock,
                bg: 'purple.100',
                color: 'purple.800',
                label: 'Needed'
            }
        };
        return statusConfig[status];
    }, [status]);

    return (
        <Box
            px={2}
            py={1}
            bg={config.bg}
            color={config.color}
            borderRadius="md"
            fontSize="xs"
            fontWeight="semibold"
            display="flex"
            alignItems="center"
            gap={1}
        >
            <Icon as={config.icon as React.ElementType} w={3} h={3} />
            {config.label}
        </Box>
    );
};

interface RekeyingTableProps {
    orders: Array<ReKeyingEntry>;
    bulkSelection?: DashboardBulkSelection;
}

const RekeyingTable = ({ orders, bulkSelection }: RekeyingTableProps) => {
    const navigate = useNavigate();
    const archiveOrder = useArchiveOrder();
    const completeOrder = useCompleteOrder();
    const { widths: colWidths, updateWidth: updateColWidth } = useColumnWidths('dashboard_re-keying');

    const sessionData = useContext(SessionContext);
    const canComplete = !!sessionData?.isAuthenticated && sessionData.permissions.includes('complete-uncomplete');
    const canArchive = !!sessionData?.isAuthenticated && sessionData.permissions.includes('archive-unarchive');

    const handleArchiveOrder = (orderId: string) => {
        archiveOrder.mutate({ orderId });
    };

    const handleCompleteOrder = (orderId: string) => {
        completeOrder.mutate({ orderId });
    };


    if (!orders || orders.length === 0) {
        return (
            <Flex
                direction="column"
                align="center"
                justify="center"
                py={12}
                px={6}
                textAlign="center"
                borderTop="1px solid"
                borderColor="gray.200"
            >
                <Icon
                    as={FaClipboardList as React.ElementType}
                    w={16}
                    h={16}
                    color="gray.300"
                    mb={6}
                />
                <Heading
                    size="lg"
                    color="gray.600"
                    mb={2}
                >
                    No re-keying orders yet
                </Heading>
                <Text
                    color="gray.500"
                    fontSize="md"
                    maxW="md"
                >
                    Orders will appear here when available for re-keying.
                </Text>
            </Flex>
        );
    }

    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit'
        });
    };

    const formatTime = (date: string) => {
        return new Date(date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        });
    };


    return (
        <Table variant="simple" size='sm' minW="100%" sx={getDashboardBulkTableSx(bulkSelection, Object.keys(colWidths).length > 0 ? { tableLayout: 'fixed', '& td': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } } : undefined)}>
            <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX}>
                <Tr bg="gray.50">
                    <ResizableTh columnKey="roNumber" storedWidth={colWidths.roNumber} onResize={updateColWidth} py={2.5} pl={getDashboardRoColumnPl(bulkSelection)} pr={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="100px" position="relative">
                        <DashboardRoSelectAllHeader bulkSelection={bulkSelection} />
                        RO Number
                    </ResizableTh>
                    <ResizableTh columnKey="vehicle" storedWidth={colWidths.vehicle} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="190px">Vehicle</ResizableTh>
                    <ResizableTh columnKey="customer" storedWidth={colWidths.customer} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="120px">Customer</ResizableTh>
                    <ResizableTh columnKey="location" storedWidth={colWidths.location} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="140px">Location</ResizableTh>
                    <ResizableTh columnKey="insurance" storedWidth={colWidths.insurance} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="140px">Insurance</ResizableTh>
                    <ResizableTh columnKey="timeOrdered" storedWidth={colWidths.timeOrdered} onResize={updateColWidth} py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="140px">Time Ordered</ResizableTh>
                    <ResizableTh columnKey="partsReKeyed" storedWidth={colWidths.partsReKeyed} onResize={updateColWidth} py={2.5} px={3} textAlign="center" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200" minW="130px">Parts Re-Keyed</ResizableTh>
                    <Th w="180px" py={2.5} px={3} textAlign="left" color="gray.500" fontSize="11px" fontWeight="semibold" letterSpacing="wider" textTransform="uppercase" borderBottomWidth="1px" borderColor="gray.200">Status</Th>
                    {canArchive && <Th py={2.5} pr={6} pl={1} textAlign="right" color="gray.500" fontSize="11px" fontWeight="semibold" borderBottomWidth="1px" borderColor="gray.200" w="60px" minW="60px"></Th>}
                </Tr>
            </Thead>
            <Tbody>
                {orders.map((order, rowIndex) => {
                    const isSelected = bulkSelection?.selectedOrderIds.has(order._id) ?? false;

                    return (
                        <Tr
                            key={order._id}
                            {...getDashboardRowHighlight(isSelected, false)}
                            onClick={(e) => {
                                handleOrderBulkRowClick(
                                    e,
                                    order._id,
                                    rowIndex,
                                    (id, idx, shift) => bulkSelection?.onToggle(id, idx, shift),
                                    () => navigate(`/order/${order._id}`, { state: { source: 'dashboard', initialStatus: 'rekey' } }),
                                    !!bulkSelection?.enabled,
                                );
                            }}
                        >
                            <Td
                                py={3}
                                pl={getDashboardRoColumnPl(bulkSelection)}
                                pr={3}
                                position={bulkSelection?.enabled ? 'relative' : undefined}
                                whiteSpace="normal"
                                wordBreak="break-word"
                                borderBottomWidth="1px"
                                borderColor="gray.100"
                            >
                                <DashboardRoColumnRow
                                    bulkSelection={bulkSelection}
                                    orderId={order._id}
                                    rowIndex={rowIndex}
                                    isChecked={isSelected}
                                >
                                    <EnterEstimateBadge canEnter={order.canEnterEstimate} />
                                    <ChakraLink
                                        as={RouterLink}
                                        to={`/order/${order._id}`}
                                        state={{ source: 'dashboard', initialStatus: 'rekey' }}
                                        onClick={(e) => e.stopPropagation()}
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color="gray.800"
                                        _hover={{ textDecoration: 'underline' }}
                                    >
                                        {order.roNumber}
                                    </ChakraLink>
                                    <NoteCountBadge noteCount={order.noteCount} orderId={order._id} />
                                    <SupplierCommunicationCountBadge count={order.supplierCommunicationCount} />
                                </DashboardRoColumnRow>
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" borderBottomWidth="1px" borderColor="gray.100">
                                <VStack align="start" spacing={0.5}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                        {order.ymm}
                                    </Text>
                                    <Vin vin={order.vin} />
                                </VStack>
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" borderBottomWidth="1px" borderColor="gray.100">
                                {order.lastName && order.firstName ?
                                    <VStack align="start" spacing={0}>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.800">
                                            {order.lastName || '—'}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {order.firstName || '—'}
                                        </Text>
                                    </VStack> :
                                    <Text fontSize="sm" color="gray.500">
                                        —
                                    </Text>
                                }
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" minW="140px" borderBottomWidth="1px" borderColor="gray.100">
                                <Text fontSize="sm" color="gray.700">
                                    {order.locationName || '—'}
                                </Text>
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" wordBreak="break-word" minW="140px" borderBottomWidth="1px" borderColor="gray.100">
                                <Text fontSize="sm" color="gray.700">
                                    {order.insurance || '—'}
                                </Text>
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" borderBottomWidth="1px" borderColor="gray.100">
                                {!order.timeOrdered ? (
                                    <Text fontSize='sm' color="gray.700">—</Text>
                                ) : (
                                    <VStack align="start" spacing={0.5}>
                                        <Text fontSize='sm' color="gray.700">
                                            {formatDate(order.timeOrdered)}
                                        </Text>
                                        <Text fontSize="xs" color="gray.500">
                                            {formatTime(order.timeOrdered)}
                                        </Text>
                                    </VStack>
                                )}
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" textAlign="center" borderBottomWidth="1px" borderColor="gray.100">
                                <Text fontSize="sm" color="gray.700" fontWeight='medium'>
                                    {order.partsReKeyed} / {order.partsConfirmed}
                                </Text>
                            </Td>
                            <Td py={3} px={3} whiteSpace="normal" textAlign="left" minW="130px" borderBottomWidth="1px" borderColor="gray.100">
                                <Flex gap={2} justify="flex-start">
                                    <RekeyingStatusBadge status={order.status} />
                                </Flex>
                            </Td>
                            {canArchive && <Td py={3} pr={6} pl={1} textAlign="right" w="60px" minW="60px" borderBottomWidth="1px" borderColor="gray.100">
                                <Menu>
                                    <MenuButton
                                        as={IconButton}
                                        aria-label="Options"
                                        icon={<Icon as={HiOutlineDotsVertical as React.ElementType} w={4} h={4} />}
                                        size="xs"
                                        variant="outline"
                                        colorScheme="gray"
                                        borderRadius="lg"
                                        borderColor="gray.300"
                                        _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                                        _active={{ bg: 'gray.100', borderColor: 'gray.400' }}
                                        _focus={{ boxShadow: 'outline' }}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <MenuList py={1} minW="200px">
                                        <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                window.open(`/order/${order._id}`, '_blank');
                                            }}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={FiExternalLink as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Open in New Tab</Text>
                                            </HStack>
                                        </MenuItem>
                                        {canComplete && order.canComplete && <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCompleteOrder(order._id);
                                            }}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={FiCheckCircle as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Mark Complete</Text>
                                            </HStack>
                                        </MenuItem>}
                                        <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleArchiveOrder(order._id);
                                            }}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: "#E6F1FD", color: "#0071E3" }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={ORDER_ACTION_ICONS.archive as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Archive Order</Text>
                                            </HStack>
                                        </MenuItem>
                                    </MenuList>
                                </Menu>
                            </Td>}
                        </Tr>
                    );
                })}
            </Tbody>
        </Table>
    );
};

export default RekeyingTable;
